import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { TApi, TSupporterRoles } from '../../../../shared/const'
import http from '../../../../shared/http'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import useIsDirty from '../../../../shared/useIsDirty'
import { askConfirm, askDiscardChanges } from '../../../../shared/confirmDialog'
import { invitationValidity } from '../../../../shared/helpers'

import Btn from '../../../../UI/Btn'
import Icon from '../../../../UI/Icon'
import Loading from '../../../../UI/Loading'
import InvitationLinkShare from '../../../../UI/InvitationLinkShare'
import { PlainInput, PlainCheckbox } from '../../../../UI/PlainField'

import styles from './inviteModal.module.scss'
import { formatDateTime } from '../../../../shared/datetime'

/**
 * Zieht aus der Antwort des Servers heraus, was zur Herkunft der Einladung
 * gehoert. Der Server liefert die einladende Person nur bei der Einzelabfrage
 * mit; bei Einladungen aus der Zeit vor dieser Erfassung ist niemand vermerkt.
 */
const readOrigin = data => ({
	invitedAt: data.invitedAt || null,
	invitedBy: data.invitedBy || null,
	source: data.source || null,
	company: data.invitedByCompany || null,
	// Erinnerungen des naechtlichen Laufs (seit 31.10.2026). „Erneut senden"
	// setzt sie zurueck.
	reminderCount: data.reminderCount || 0,
	lastReminderAt: data.lastReminderAt || null,
	// Derselbe Link wie in der Mail - zum Weitergeben aus dem eigenen Postfach.
	activationLink: data.activationLink || null,
	// Die eingestellte Gültigkeit (14–90 Tage) - fehlt sie, gelten 60.
	validDays: data.validDays || 60
})

/**
 * Stammdaten einer noch offenen Einladung ansehen und korrigieren.
 *
 * Eine Einladung hat noch kein Profil - bis zur Aktivierung liegen Name, Adresse
 * und Rollenvorschlag nur an der Einladung. Bisher liess sich daran nichts mehr
 * aendern: ein Tippfehler bedeutete zuruecknehmen und neu einladen.
 *
 * Wird die Adresse geaendert, verschickt der Server die Einladung an die neue
 * Adresse erneut und entwertet den alten Link. Darauf weist die Maske hin,
 * damit die Mail niemanden ueberrascht.
 *
 * Ueber den Stammdaten steht, woher die Einladung kommt: von wem, wann, ueber
 * welchen Weg und wie lange sie noch gilt. Ohne diese Angaben war an einer
 * offenen Einladung nicht erkennbar, wer sie ausgesprochen hat - bei mehreren
 * Bundes- und Landesadmins und den Einladungen der Junior Companies ist das die
 * erste Rueckfrage. Darunter stehen die beiden Handlungen, die es zu einer
 * Einladung gibt: erneut senden und zurueckziehen. Beide gab es bisher nur im
 * Drei-Punkte-Menue der Liste (das Zuruecknehmen) oder gar nicht (das erneute
 * Senden - die Route dafuer lag ungenutzt im Server).
 */
export default function EditInvitationModal({ invitationId, onClose, onSaved, onRemoved }) {
	const { t } = useTranslation()

	// Rollen stehen mit in `values` und nicht daneben: React 17 fasst die
	// Zustandswechsel aus einer Antwort des Servers nicht zusammen. Lagen sie
	// getrennt, gab es einen Zwischenstand mit Stammdaten und noch ohne Rollen -
	// den nahm `useIsDirty` als Vergleichsstand, und das Fenster hielt sich von
	// der ersten Sekunde an fuer geaendert.
	const [values, setValues] = useState(null)
	const [initialEmail, setInitialEmail] = useState('')
	// Herkunft der Einladung: wer, wann, woher. Steht getrennt von `values`,
	// weil daran nichts zu aendern ist - es wird nur berichtet.
	const [origin, setOrigin] = useState(null)
	const senderName = useSelector(state =>
		[state.user?.profile?.firstName, state.user?.profile?.lastName].filter(Boolean).join(' ')
	)
	const [isSaving, setSaving] = useState(false)
	const [isBusy, setBusy] = useState(false)
	const [errors, setErrors] = useState({})

	useEffect(() => {
		if (!invitationId) return

		setValues(null)
		setErrors({})
		http.get(TApi.ADMIN_SUPPORTER_INVITATION_ID(invitationId))
			.then(({ data }) => {
				setValues({
					email: data.email || '',
					firstName: data.firstName || '',
					lastName: data.lastName || '',
					company: data.company || '',
					position: data.position || '',
					roles: data.roles?.length ? data.roles : [TSupporterRoles.EXPERT]
				})
				setInitialEmail(data.email || '')
				setOrigin(readOrigin(data))
			})
			.catch(() => {
				// Meldung kommt aus shared/http
				onClose()
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [invitationId])

	// Das Fenster bleibt in der Liste haengen, auch wenn es zu ist - `values`
	// haelt danach weiter die Werte der zuletzt gezeigten Einladung. Beim
	// Oeffnen wird deshalb hier zurueckgesetzt, waehrend des Renderns und nicht
	// erst im Effekt: Der Effekt laeuft nach dem Rendern, und der Durchlauf
	// dazwischen reichte `useIsDirty` schon den alten Stand als Vergleich. Wer
	// eine Einladung bearbeitet, verworfen und gleich wieder geoeffnet hatte,
	// wurde beim Schliessen erneut nach ungespeicherten Aenderungen gefragt.
	const gezeigteEinladung = useRef(invitationId)
	if (gezeigteEinladung.current !== invitationId) {
		gezeigteEinladung.current = invitationId
		setValues(null)
	}

	const isDirty = useIsDirty(values, invitationId)

	const requestClose = async () => {
		if (isDirty && !(await askDiscardChanges())) return
		onClose()
	}

	const backdropProps = useDialogDismiss(requestClose, { enabled: Boolean(invitationId) })

	if (!invitationId) return null

	const setValue = (key, value) => setValues(current => ({ ...current, [key]: value }))

	const toggleRole = role =>
		setValues(current => ({
			...current,
			roles: current.roles.includes(role) ? current.roles.filter(item => item !== role) : [...current.roles, role]
		}))

	const validate = () => {
		const next = {}
		if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) next.email = t('Invalid email')
		if (values.firstName.trim().length < 2) next.firstName = t('Required field')
		if (values.lastName.trim().length < 2) next.lastName = t('Required field')
		if (!values.roles.length) next.roles = t('Required field')

		setErrors(next)
		return !Object.keys(next).length
	}

	const submit = event => {
		event.preventDefault()
		if (!validate()) return

		setSaving(true)
		http.put(TApi.ADMIN_SUPPORTER_INVITATION_ID(invitationId), values)
			.then(({ data }) => {
				// Der Server meldet zurueck, ob wegen der neuen Adresse eine Mail
				// hinausging und ob sie ankam - sonst bliebe das unbemerkt.
				if (data?.emailResent && data?.emailSent === false) {
					toast.warning(t('Invitation saved, but the email could not be sent'))
				} else if (data?.emailResent) {
					toast.success(t('invitationResentToNewAddress'))
				} else {
					toast.success(t('Saved successfully'))
				}
				onSaved()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setSaving(false))
	}

	/**
	 * Verschickt die Einladung noch einmal.
	 *
	 * Der alte Aktivierungslink verliert dabei seine Gueltigkeit und die Frist
	 * beginnt neu - deshalb aktualisiert die Maske anschliessend die Herkunft
	 * aus der Antwort, sonst stuende dort weiter das alte Datum.
	 */
	const resend = () => {
		setBusy(true)
		http.post(TApi.ADMIN_SUPPORTER_INVITATION_RESEND(invitationId))
			.then(({ data }) => {
				if (data?.emailSent === false) {
					toast.warning(t('invitationResentButEmailFailed'))
				} else {
					toast.success(t('Invitation resent successfully'))
				}
				setOrigin(readOrigin(data))
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setBusy(false))
	}

	/** Zieht die Einladung zurueck - dieselbe Handlung wie in der Liste. */
	const withdraw = async () => {
		const confirmed = await askConfirm({
			title: t('cancelInvitation'),
			text: t('cancelInvitationHint'),
			confirmLabel: t('cancelInvitation'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setBusy(true)
		http.delete(TApi.ADMIN_SUPPORTER_INVITATION_ID(invitationId))
			.then(() => {
				toast.success(t('Invitation deleted'))
				onRemoved()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setBusy(false))
	}

	const emailChanged = Boolean(values) && values.email.trim().toLowerCase() !== initialEmail

	const validity = invitationValidity(origin?.invitedAt, origin?.validDays)

	/**
	 * Eine Angabe zur Herkunft. Ist nichts bekannt, steht dort "nicht erfasst"
	 * und nicht der uebliche Strich: Der Strich sieht aus, als waere etwas
	 * verloren gegangen - erfasst wurde es nie.
	 */
	const fact = (label, value, warn = false) => (
		<div className={styles.fact}>
			<span className={styles.factLabel}>{label}</span>
			<span className={clsx(styles.factValue, warn && styles.factWarn)}>{value || t('notRecorded')}</span>
		</div>
	)

	const formatMoment = value =>
		formatDateTime(value, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		}) || null

	// Name und Rolle: "Landesadmin" allein sagt bei neun Bundeslaendern zu wenig,
	// der Name allein sagt nicht, aus welcher Ecke die Einladung kam.
	const inviterLabel = () => {
		const person = origin?.invitedBy
		if (!person) return null

		const name = [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email
		const role = person.role ? t(`invitedByRole${person.role}`) : ''
		return role ? `${name} (${role})` : name
	}

	// Ueber welchen Weg: die Verwaltung oder eine Junior Company. Bei der Junior
	// Company gehoert ihr Name dazu - die Lehrkraft laedt in deren Namen ein.
	const sourceLabel = () => {
		if (!origin?.source) return null
		const label = t(`invitationSource${origin.source}`)
		return origin.company?.name ? `${label} — ${origin.company.name}` : label
	}

	const remindersLabel = () =>
		origin?.reminderCount
			? t('invitationRemindersCount', {
					count: origin.reminderCount,
					date: formatMoment(origin.lastReminderAt)
			  })
			: t('invitationRemindersNone')

	const validityLabel = () => {
		if (!validity) return null
		return validity.expired ? t('Expired') : t('Valid for {{days}} more days', { days: validity.daysLeft })
	}

	return (
		<div className={styles.overlay} {...backdropProps}>
			{!values ? (
				<div className={styles.modal}>
					<Loading />
				</div>
			) : (
				<form className={styles.modal} onSubmit={submit}>
					{/*
					 * Das Kreuz steht oben rechts, wo es in jedem anderen Fenster
					 * der Anwendung steht. Es schliesst wie der Klick daneben und
					 * wie Esc - alle drei Wege gehen ueber `requestClose` und
					 * fragen nur nach, wenn wirklich etwas geaendert wurde.
					 */}
					<div className={styles.header}>
						<h2 className={styles.title}>{t('editInvitation')}</h2>
						<button
							type="button"
							className={styles.btnClose}
							aria-label={t('Close')}
							onClick={requestClose}
						>
							<Icon name="close" size={{ w: 12, h: 12 }} />
						</button>
					</div>
					<p className={styles.hint}>{t('editInvitationHint')}</p>

					{/*
					 * Die Herkunft steht vor den Stammdaten: Sie beantwortet die
					 * Frage, die man an einer offenen Einladung zuerst hat, und ist
					 * nichts zum Ausfuellen - deshalb als Angaben, nicht als Felder.
					 */}
					<div className={styles.origin}>
						<span className={styles.originLabel}>{t('invitationOrigin')}</span>
						<div className={styles.facts}>
							{fact(t('Invited by'), inviterLabel())}
							{fact(t('invitationSource'), sourceLabel())}
							{fact(t('Invited on'), formatMoment(origin?.invitedAt))}
							{fact(t('Validity'), validityLabel(), validity?.expired)}
							{fact(t('invitationReminders'), remindersLabel())}
						</div>
					</div>

					{!validity?.expired && (
						<InvitationLinkShare
							link={origin?.activationLink}
							email={initialEmail}
							recipientName={[values.firstName, values.lastName].filter(Boolean).join(' ')}
							companyName={origin?.company?.name}
							senderName={senderName}
							validDays={origin?.validDays}
						/>
					)}

					<div className={styles.grid}>
						<PlainInput
							label={t('firstName')}
							value={values.firstName}
							error={errors.firstName}
							onChange={e => setValue('firstName', e.target.value)}
						/>
						<PlainInput
							label={t('lastName')}
							value={values.lastName}
							error={errors.lastName}
							onChange={e => setValue('lastName', e.target.value)}
						/>
						<PlainInput
							label={t('email')}
							value={values.email}
							error={errors.email}
							onChange={e => setValue('email', e.target.value)}
						/>
						<PlainInput
							label={t('company')}
							value={values.company}
							onChange={e => setValue('company', e.target.value)}
						/>
						<PlainInput
							label={t('position')}
							value={values.position}
							onChange={e => setValue('position', e.target.value)}
						/>
					</div>

					{emailChanged && <p className={styles.warning}>{t('invitationEmailChangedHint')}</p>}

					<div className={styles.roles}>
						<span className={styles.rolesLabel}>{t('possibleRoles')}</span>
						<div className={styles.roleList}>
							{Object.values(TSupporterRoles).map(role => (
								<PlainCheckbox
									key={role}
									checked={values.roles.includes(role)}
									onChange={() => toggleRole(role)}
									label={t(`supporterRole${role}`)}
								/>
							))}
						</div>
						{errors.roles && <span className={styles.error}>{errors.roles}</span>}
					</div>

					{/*
					 * Alle vier Handlungen stehen in einer Zeile: links, was mit der
					 * Einladung selbst geschieht, rechts das Speichern der
					 * Stammdaten. Der Zwischenraum haelt das Zuruecknehmen von
					 * "Speichern" fern, ohne sie in zwei Zeilen zu brechen.
					 */}
					<div className={styles.footer}>
						<div className={styles.footerActions}>
							<Btn type="button" theme="secondary" loading={isBusy} onClick={resend}>
								{t('Resend invitation')}
							</Btn>
							<Btn type="button" theme="danger" loading={isBusy} onClick={withdraw}>
								{t('cancelInvitation')}
							</Btn>
						</div>
						<div className={styles.footerActions}>
							<Btn type="button" theme="secondary" onClick={requestClose}>
								{t('Cancel')}
							</Btn>
							<Btn type="submit" disabled={isSaving || isBusy}>
								{t('save')}
							</Btn>
						</div>
					</div>
				</form>
			)}
		</div>
	)
}
